/**
 * Module Configuration
 * Defines the 4 main modules and their navigation structure
 * Feature flags controlled via environment variables
 */

// Module definitions
export const MODULES = {
  SALES: 'sales',
  PROGRESSION: 'progression',
  LETTINGS: 'lettings',
  MAINTENANCE: 'maintenance',
  ACCOUNTS: 'accounts', // Restricted module
};

// Feature flags from environment - determines if module is enabled
// Admin/Director can always access disabled modules for internal testing
export const MODULE_FLAGS = {
  [MODULES.SALES]: process.env.REACT_APP_MODULE_SALES === 'on',
  [MODULES.PROGRESSION]: process.env.REACT_APP_MODULE_PROGRESSION === 'on',
  [MODULES.LETTINGS]: process.env.REACT_APP_MODULE_LETTINGS === 'on',
  [MODULES.MAINTENANCE]: process.env.REACT_APP_MODULE_MAINTENANCE === 'on',
  [MODULES.ACCOUNTS]: process.env.REACT_APP_MODULE_ACCOUNTS === 'on',
};

// Roles that bypass feature flags (internal testing)
export const FEATURE_FLAG_BYPASS_ROLES = ['director', 'admin'];

// Helper to check if module is enabled for user
export const isModuleEnabled = (module, user) => {
  // Module is globally enabled
  if (MODULE_FLAGS[module]) return true;
  
  // Admin/Director can access disabled modules for testing
  if (user && FEATURE_FLAG_BYPASS_ROLES.includes(user.role)) return true;
  
  return false;
};

// Module metadata for launcher cards
export const MODULE_INFO = {
  [MODULES.SALES]: {
    id: MODULES.SALES,
    title: 'Sales',
    description: 'Instructions, listings, applicants & offers',
    icon: 'Home',
    color: 'emerald',
    defaultPath: '/sales',
    badgeKeys: ['active_listings', 'pending_offers', 'new_applicants'],
  },
  [MODULES.PROGRESSION]: {
    id: MODULES.PROGRESSION,
    title: 'Sales Progression',
    description: 'Cases, chains, milestones & parties',
    icon: 'GitBranch',
    color: 'blue',
    defaultPath: '/progression',
    badgeKeys: ['active_cases', 'pending_milestones', 'awaiting_exchange'],
  },
  [MODULES.LETTINGS]: {
    id: MODULES.LETTINGS,
    title: 'Lettings',
    description: 'Properties, landlords, tenants & compliance',
    icon: 'Building2',
    color: 'violet',
    defaultPath: '/lettings',
    badgeKeys: ['managed_properties', 'tenancies', 'compliance_due'],
  },
  [MODULES.MAINTENANCE]: {
    id: MODULES.MAINTENANCE,
    title: 'Maintenance',
    description: 'Jobs, quotes, contractors & invoices',
    icon: 'Wrench',
    color: 'amber',
    defaultPath: '/maintenance',
    badgeKeys: ['open_jobs', 'pending_quotes', 'unpaid_invoices'],
  },
  [MODULES.ACCOUNTS]: {
    id: MODULES.ACCOUNTS,
    title: 'Accounts',
    description: 'Statements, payments, rent ledger & commission',
    icon: 'PoundSterling',
    color: 'slate',
    defaultPath: '/accounts',
    badgeKeys: ['pending_statements', 'unpaid_invoices'],
    restricted: true,
  },
};

// Roles that can access Accounts module
export const ACCOUNTS_ALLOWED_ROLES = ['director', 'admin', 'accounts', 'accounts_admin'];

// Navigation items for each module
export const MODULE_NAV = {
  [MODULES.SALES]: [
    { path: '/sales', label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: '/sales/simple', label: 'Quick Add', icon: 'Zap' },
    { path: '/sales/instructions/list', label: 'Instructions', icon: 'FileText' },
    { path: '/sales/instructions/list', label: 'Properties', icon: 'Home' },
    { path: '/sales', label: 'Applicants', icon: 'Users', tab: 'applicants' },
    { path: '/sales/instructions/list', label: 'Offers', icon: 'HandCoins' },
    { path: '/sales', label: 'Documents', icon: 'FolderOpen', tab: 'documents' },
  ],
  [MODULES.PROGRESSION]: [
    { path: '/progression', label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: '/progression', label: 'Cases', icon: 'Briefcase', tab: 'cases' },
    { path: '/sales/chain', label: 'Chains', icon: 'GitBranch' },
    { path: '/progression', label: 'Milestones', icon: 'Flag', tab: 'milestones' },
    { path: '/sales/solicitors', label: 'Parties', icon: 'Users' },
    { path: '/progression', label: 'Updates', icon: 'MessageSquare', tab: 'updates' },
    { path: '/progression', label: 'Documents', icon: 'FolderOpen', tab: 'documents' },
  ],
  [MODULES.LETTINGS]: [
    { path: '/lettings', label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: '/admin/lettings', label: 'Properties', icon: 'Building2' },
    { path: '/lettings', label: 'Landlords', icon: 'User', tab: 'landlords' },
    { path: '/lettings', label: 'Tenants', icon: 'Users', tab: 'tenants' },
    { path: '/admin/compliance', label: 'Compliance', icon: 'ShieldCheck' },
    { path: '/admin/inspections', label: 'Inspections', icon: 'ClipboardCheck' },
  ],
  [MODULES.MAINTENANCE]: [
    { path: '/maintenance', label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: '/maintenance', label: 'Jobs', icon: 'Wrench', tab: 'jobs' },
    { path: '/maintenance?tab=issues', label: 'Issues', icon: 'AlertTriangle', tab: 'issues', badgeKey: 'open_issues' },
    { path: '/maintenance', label: 'Quotes', icon: 'FileText', tab: 'quotes' },
    { path: '/maintenance', label: 'Contractors', icon: 'HardHat', tab: 'contractors' },
    { path: '/maintenance', label: 'Invoices', icon: 'Receipt', tab: 'invoices' },
    { path: '/diary', label: 'Diary', icon: 'Calendar' },
  ],
  [MODULES.ACCOUNTS]: [
    { path: '/accounts', label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: '/accounts/services', label: 'Landlord Statements', icon: 'FileText' },
    { path: '/accounts/invoicing', label: 'Contractor Payments', icon: 'Receipt' },
    { path: '/admin/lettings/rent', label: 'Rent Ledger', icon: 'Wallet' },
    { path: '/commission', label: 'Commission', icon: 'PoundSterling' },
    { path: '/accounts/xero', label: 'Xero Sync', icon: 'RefreshCw' },
  ],
};

// Tools menu items (attached to relevant modules)
export const MODULE_TOOLS = {
  [MODULES.SALES]: [
    { path: '/commission', label: 'Commission', icon: 'PoundSterling', roles: ['admin', 'director', 'sales_agent', 'sales_negotiator'] },
    { path: '/marketing/properties', label: 'Marketing', icon: 'Globe' },
    { path: '/admin/reports', label: 'Reports', icon: 'BarChart3', roles: ['admin', 'director'] },
  ],
  [MODULES.PROGRESSION]: [
    { path: '/sales/solicitors', label: 'Solicitor Firms', icon: 'Building' },
    { path: '/commission', label: 'Commission', icon: 'PoundSterling', roles: ['admin', 'director', 'sales_negotiator'] },
    { path: '/admin/reports', label: 'Reports', icon: 'BarChart3', roles: ['admin', 'director'] },
  ],
  [MODULES.LETTINGS]: [
    { path: '/inventory', label: 'Inventory', icon: 'ClipboardList', roles: ['admin', 'director', 'inventory_clerk', 'lettings_manager'] },
    { path: '/commission', label: 'Commission', icon: 'PoundSterling', roles: ['admin', 'director', 'lettings_manager', 'lettings_negotiator'] },
    { path: '/admin/lettings/analytics', label: 'Analytics', icon: 'BarChart3', roles: ['admin', 'director', 'lettings_manager'] },
    { path: '/admin/reports', label: 'Reports', icon: 'BarChart3', roles: ['admin', 'director'] },
  ],
  [MODULES.MAINTENANCE]: [
    { path: '/admin/maintenance/analytics', label: 'Analytics', icon: 'BarChart3', roles: ['admin', 'director'] },
    { path: '/admin/reports', label: 'Reports', icon: 'BarChart3', roles: ['admin', 'director'] },
  ],
  [MODULES.ACCOUNTS]: [
    { path: '/commission', label: 'Commission', icon: 'PoundSterling' },
    { path: '/admin/reports', label: 'Reports', icon: 'BarChart3', roles: ['admin', 'director'] },
  ],
};

// Admin/Settings items (accessible from user menu)
export const ADMIN_ITEMS = [
  { path: '/staff', label: 'Staff Management', icon: 'Users', roles: ['admin', 'director'] },
  { path: '/admin/sync', label: 'Data Sync', icon: 'RefreshCw', roles: ['admin'] },
  { path: '/contacts', label: 'Contacts', icon: 'Contact' },
];

// Helper to determine module from current path
export const getModuleFromPath = (pathname) => {
  if (pathname.startsWith('/sales') && !pathname.startsWith('/sales/chain')) {
    return MODULES.SALES;
  }
  if (pathname.startsWith('/progression') || pathname.startsWith('/sales/chain') || pathname.startsWith('/sales/case')) {
    return MODULES.PROGRESSION;
  }
  if (pathname.startsWith('/lettings') || pathname.startsWith('/admin/lettings') || pathname.startsWith('/admin/compliance') || pathname.startsWith('/admin/inspections')) {
    return MODULES.LETTINGS;
  }
  if (pathname.startsWith('/maintenance') || pathname.startsWith('/contractor') || pathname.startsWith('/diary')) {
    return MODULES.MAINTENANCE;
  }
  if (pathname.startsWith('/accounts') || pathname.startsWith('/commission')) {
    return MODULES.ACCOUNTS;
  }
  return null;
};

// Helper to check if user can access a module
export const canAccessModule = (module, user) => {
  if (!user) return false;
  
  // Check feature flag first
  if (!isModuleEnabled(module, user)) {
    return false;
  }
  
  // Accounts is restricted to specific roles
  if (module === MODULES.ACCOUNTS) {
    return ACCOUNTS_ALLOWED_ROLES.includes(user.role);
  }
  
  // All staff can access other enabled modules
  const staffRoles = [
    'admin', 'director', 'sales_agent', 'sales_negotiator', 'sales_progressor',
    'lettings_manager', 'lettings_negotiator', 'accounts', 'accounts_admin', 'inventory_clerk'
  ];
  
  return staffRoles.includes(user.role);
};

// Helper to get modules user can access
export const getAccessibleModules = (user) => {
  if (!user) return [];
  
  return Object.values(MODULES).filter(module => canAccessModule(module, user));
};

// Helper to check if module is disabled (for "Coming Soon" display)
export const isModuleDisabled = (module, user) => {
  return !isModuleEnabled(module, user);
};
